'use client'

import { getTokenImageUrl } from '@/domain/euler'
import {
  isParallelIncentivizedBorrowPair,
  useParallelBorrowIncentives,
} from '@/domain/hooks/useParallelBorrowIncentives'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { useCardSolverConfigs, useStoreHydrated } from '@/domain/state'
import { CardRouteBadge } from '@/features/card/components/CardRouteBadge'
import {
  getEulerBorrowCardRouteBadge,
  getPooledBorrowCardRouteBadge,
} from '@/features/card/utils/cardSolverRouteMatching'
import { getEntities } from '@/features/evault-details/data/entities'
import { formatMarketsDisplay } from '@/features/evault-details/data/products'
import { assets } from '@/ui/assets'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { Tooltip, TooltipContentLong, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { TooltipContentLayout } from '@/ui/atoms/tooltip/TooltipContentLayout'
import { Dash } from '@/ui/atoms/dash/Dash'
import { InWalletCell } from '@/ui/organisms/market-entry/InWalletCell'
import { cn } from '@/ui/utils/style'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useAccount } from 'wagmi'
import { getPairPointsInfo } from '../logic/getPairPointsInfo'
import type { BorrowPair } from '../logic/useBorrowPairs'
import { BORROW_VAULT_CHAIN_ID } from '../logic/useVaultList'
import { ParallelIncentiveBadge } from './ParallelIncentiveBadge'

const DEFAULT_CURATOR_LOGO = '/hypurr-paw.svg'
type PointsType = 'euler' | 'pooled'
type PointDetailRow = {
  title: string
  icon: string
  multiplier?: number
  sourceLabel: 'Borrow' | 'Collateral'
}

function getCuratorLogo(entityKey: string | string[] | undefined): string {
  if (!entityKey) return DEFAULT_CURATOR_LOGO
  const entities = getEntities()
  const key = Array.isArray(entityKey) ? entityKey[0] : entityKey
  if (!key) return DEFAULT_CURATOR_LOGO
  const entity = entities.get(key)
  return entity?.logo || DEFAULT_CURATOR_LOGO
}

function getCuratorName(entityKey: string | string[] | undefined): string {
  if (!entityKey) return 'HypurrFi'
  const entities = getEntities()
  const key = Array.isArray(entityKey) ? entityKey[0] : entityKey
  if (!key) return 'HypurrFi'
  const entity = entities.get(key)
  return entity?.name || 'HypurrFi'
}

function getPointDetailRows(
  pair: ExtendedBorrowPair,
  getByAddress: ReturnType<typeof usePointsData>['getByAddress'],
  borrowContextPoints: ReturnType<typeof getPairPointsInfo>['partnerPoints'],
): PointDetailRow[] {
  const rows: PointDetailRow[] = []
  const pointsType: PointsType = pair.routeType === 'pooled' ? 'pooled' : 'euler'
  const hypurrBorrowPoints = getByAddress({
    type: pointsType,
    action: 'borrow',
    address: pair.liability,
  })
  const hypurrCollateralPoints = getByAddress({
    type: pointsType,
    action: 'deposit',
    address: pair.collateral,
  })

  if (hypurrBorrowPoints?.multiplier) {
    rows.push({
      title: 'HypurrFi Points',
      icon: 'hypurrPawNoShadow',
      multiplier: hypurrBorrowPoints.multiplier,
      sourceLabel: 'Borrow',
    })
  }

  if (hypurrCollateralPoints?.multiplier) {
    rows.push({
      title: 'HypurrFi Points',
      icon: 'hypurrPawNoShadow',
      multiplier: hypurrCollateralPoints.multiplier,
      sourceLabel: 'Collateral',
    })
  }

  for (const partner of borrowContextPoints) {
    if (partner.borrow) {
      rows.push({
        title: partner.title,
        icon: partner.icon,
        multiplier: partner.borrow.multiplier,
        sourceLabel: 'Borrow',
      })
    }

    const collateralPoints = partner.deposit ?? partner.collateral
    if (collateralPoints) {
      rows.push({
        title: partner.title,
        icon: partner.icon,
        multiplier: collateralPoints.multiplier,
        sourceLabel: 'Collateral',
      })
    }
  }

  return rows
}

interface ExtendedBorrowPair extends BorrowPair {
  routeType?: 'euler' | 'pooled'
  clusterName?: string
  walletBalanceAmount?: number
}

export interface VirtualBorrowTableProps {
  pairs: ExtendedBorrowPair[]
  showInWallet?: boolean
  showBorrowed?: boolean
  showLeverageMetrics?: boolean
}

type SortField = 'apy' | 'points' | 'ltv' | 'liquidity' | 'utilization' | 'position' | 'wallet'
type SortDirection = 'asc' | 'desc'

export function VirtualBorrowTable({
  pairs,
  showInWallet = false,
  showBorrowed = false,
  showLeverageMetrics = false,
}: VirtualBorrowTableProps) {
  const { address } = useAccount()
  const listRef = useRef<HTMLDivElement>(null)
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const { getByAddress } = usePointsData()
  const { byAddress: cardSolverConfigsByAddress } = useCardSolverConfigs()
  const isStoreHydrated = useStoreHydrated()
  const parallelBorrowIncentives = useParallelBorrowIncentives()
  const [listOffsetTop, setListOffsetTop] = useState(0)
  const cardSolverConfigs = useMemo(
    () => (isStoreHydrated && address ? Object.values(cardSolverConfigsByAddress[address.toLowerCase()] ?? {}) : []),
    [address, cardSolverConfigsByAddress, isStoreHydrated],
  )

  // Reset internal sort state when leverage mode changes to use parent's sort order
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset sort when leverage mode changes
  useEffect(() => {
    setSortField(null)
    setSortDirection('desc')
  }, [showLeverageMetrics])

  useLayoutEffect(() => {
    const updateOffset = () => {
      if (listRef.current) {
        setListOffsetTop(listRef.current.offsetTop)
      }
    }

    updateOffset()
    window.addEventListener('resize', updateOffset)
    return () => window.removeEventListener('resize', updateOffset)
  }, [])

  const sortedPairs = useMemo(() => {
    // When no column header is clicked, trust the parent's sort order entirely.
    // The parent already sorts by ROE in leverage mode and by rank otherwise.
    if (sortField === null) {
      return pairs
    }

    // When user clicked a column header, apply that specific sort
    return [...pairs].sort((a, b) => {
      // Positions always come first (except in leverage mode where ROE ordering takes priority)
      if (!showLeverageMetrics) {
        const aHasPosition = (a.userBorrowAmount ?? 0) > 0
        const bHasPosition = (b.userBorrowAmount ?? 0) > 0
        if (aHasPosition && !bHasPosition) return -1
        if (!aHasPosition && bHasPosition) return 1
      }

      let aVal = 0
      let bVal = 0

      switch (sortField) {
        case 'apy': {
          // Use precomputed maxRoe in leverage mode
          aVal = showLeverageMetrics ? a.maxRoe ?? 0 : a.borrowAPY ?? 0
          bVal = showLeverageMetrics ? b.maxRoe ?? 0 : b.borrowAPY ?? 0
          break
        }
        case 'points': {
          const getPointsRank = (pair: ExtendedBorrowPair) => {
            const { hasHypurrPoints, partnerPoints } = getPairPointsInfo(pair, getByAddress)
            if (hasHypurrPoints) return 2
            if ((partnerPoints?.length ?? 0) > 0) return 1
            return 0
          }
          aVal = getPointsRank(a)
          bVal = getPointsRank(b)
          break
        }
        case 'ltv':
          aVal = Number(a.maxLTV ?? 0n)
          bVal = Number(b.maxLTV ?? 0n)
          break
        case 'liquidity':
          aVal = a.liquidityUSD ?? 0
          bVal = b.liquidityUSD ?? 0
          break
        case 'utilization':
          aVal = a.utilization ?? 0
          bVal = b.utilization ?? 0
          break
        case 'position':
          aVal = a.userBorrowAmountUsd ?? 0
          bVal = b.userBorrowAmountUsd ?? 0
          break
        case 'wallet':
          aVal = a.walletBalanceAmount ?? 0
          bVal = b.walletBalanceAmount ?? 0
          break
      }

      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal
    })
  }, [getByAddress, pairs, sortField, sortDirection, showLeverageMetrics])

  const virtualizer = useWindowVirtualizer({
    count: sortedPairs.length,
    estimateSize: () => 72,
    overscan: 5,
    scrollMargin: listOffsetTop,
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    const isActive = sortField === field
    return (
      <div className="-space-y-1 flex flex-col">
        <ChevronUp
          className={cn(
            'h-3 w-3',
            isActive && sortDirection === 'asc' ? 'text-foreground' : 'text-muted-foreground/40',
          )}
        />
        <ChevronDown
          className={cn(
            'h-3 w-3',
            isActive && sortDirection === 'desc' ? 'text-foreground' : 'text-muted-foreground/40',
          )}
        />
      </div>
    )
  }

  const gridCols = useMemo(() => {
    const cols = ['minmax(0,1.2fr)']
    cols.push('minmax(0,1fr)')
    cols.push('minmax(0,1fr)')
    cols.push('minmax(0,1fr)')
    cols.push('minmax(0,1fr)')
    cols.push('minmax(0,0.6fr)')
    if (showInWallet) cols.push('minmax(0,1fr)')
    if (showBorrowed) cols.push('minmax(0,1fr)')
    return cols.join(' ')
  }, [showInWallet, showBorrowed])

  const getDestination = (pair: ExtendedBorrowPair) => {
    if (pair.routeType === 'pooled') {
      return `/markets/pooled/${BORROW_VAULT_CHAIN_ID}/${pair.liability}`
    }
    const params = new URLSearchParams()
    if (pair.firstSubAccountWithPosition !== undefined) {
      params.set('subAccountId', String(pair.firstSubAccountWithPosition))
    }
    if (showLeverageMetrics) {
      params.set('multiply', 'true')
    }
    const queryString = params.toString()
    return `/markets/eborrow/${pair.collateral}/${pair.liability}${queryString ? `?${queryString}` : ''}`
  }

  const getPairId = (pair: ExtendedBorrowPair) => `${pair.collateral}-${pair.liability}`

  return (
    <div className="flex flex-col rounded-2xl border border-border bg-panel">
      <div
        className="sticky top-12 z-10 grid items-center gap-3 rounded-t-2xl border-border border-b bg-card px-5 pt-5 pb-2 text-muted-foreground text-xs"
        style={{ gridTemplateColumns: gridCols }}
      >
        <div>Pair</div>
        <button
          type="button"
          className={cn(
            'flex items-center justify-end gap-1 hover:text-foreground',
            sortField === 'apy' && 'text-foreground',
          )}
          onClick={() => handleSort('apy')}
        >
          {showLeverageMetrics ? 'ROE' : 'APY'} <SortIcon field="apy" />
        </button>
        <button
          type="button"
          className={cn(
            'flex items-center justify-end gap-1 hover:text-foreground',
            sortField === 'ltv' && 'text-foreground',
          )}
          onClick={() => handleSort('ltv')}
        >
          LTV / Liq. <SortIcon field="ltv" />
        </button>
        <button
          type="button"
          className={cn(
            'flex items-center justify-end gap-1 hover:text-foreground',
            sortField === 'liquidity' && 'text-foreground',
          )}
          onClick={() => handleSort('liquidity')}
        >
          Liquidity <SortIcon field="liquidity" />
        </button>
        <button
          type="button"
          className={cn(
            'flex items-center justify-end gap-1 hover:text-foreground',
            sortField === 'utilization' && 'text-foreground',
          )}
          onClick={() => handleSort('utilization')}
        >
          Util. <SortIcon field="utilization" />
        </button>
        <button
          type="button"
          className={cn(
            'flex items-center justify-end gap-1 hover:text-foreground',
            sortField === 'points' && 'text-foreground',
          )}
          onClick={() => handleSort('points')}
        >
          Points <SortIcon field="points" />
        </button>
        {showInWallet && (
          <button
            type="button"
            className={cn(
              'flex items-center justify-end gap-1 hover:text-foreground',
              sortField === 'wallet' && 'text-foreground',
            )}
            onClick={() => handleSort('wallet')}
          >
            In Wallet <SortIcon field="wallet" />
          </button>
        )}
        {showBorrowed && (
          <button
            type="button"
            className={cn(
              'flex items-center justify-end gap-1 hover:text-foreground',
              sortField === 'position' && 'text-foreground',
            )}
            onClick={() => handleSort('position')}
          >
            Position <SortIcon field="position" />
          </button>
        )}
      </div>

      <div
        ref={listRef}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const pair = sortedPairs[virtualRow.index]
          if (!pair) return null

          const hasPosition = (pair.userBorrowAmount ?? 0) > 0
          const collateralImage = getTokenImageUrl(pair.collateralTokenInfo, pair.collateralSymbol || '')
          const liabilityImage = getTokenImageUrl(pair.liabilityTokenInfo, pair.liabilitySymbol || '')

          const marketDisplay = formatMarketsDisplay(pair.collateralMarket, pair.liabilityMarket)
          const entityKey = pair.collateralMarket?.entity || pair.liabilityMarket?.entity
          const curatorName = getCuratorName(entityKey)
          const curatorLogo = getCuratorLogo(entityKey)
          const displayName = marketDisplay?.text || pair.clusterName || curatorName

          const maxLTVPercent =
            pair.maxLTV !== undefined && typeof pair.maxLTV === 'bigint' && pair.maxLTV > 0n
              ? (Number(pair.maxLTV) / 100).toFixed(0)
              : '-'
          const liqLTVPercent =
            pair.liquidationLTV !== undefined && typeof pair.liquidationLTV === 'bigint' && pair.liquidationLTV > 0n
              ? (Number(pair.liquidationLTV) / 100).toFixed(0)
              : '-'

          const liquidityDisplay =
            pair.liquidityUSD !== undefined
              ? pair.liquidityUSD >= 1_000_000
                ? `$${(pair.liquidityUSD / 1_000_000).toFixed(1)}M`
                : pair.liquidityUSD >= 1_000
                  ? `$${(pair.liquidityUSD / 1_000).toFixed(0)}K`
                  : `$${pair.liquidityUSD.toFixed(0)}`
              : '-'

          const hasParallelIncentive =
            isParallelIncentivizedBorrowPair(pair.collateral, pair.liability) && !!parallelBorrowIncentives
          const netRate =
            pair.supplyAPY !== undefined && pair.borrowAPY !== undefined ? pair.supplyAPY - pair.borrowAPY : undefined
          const netRateDisplay = netRate !== undefined ? `${netRate >= 0 ? '+' : ''}${netRate.toFixed(2)}%` : undefined

          const { hasHypurrPoints, partnerPoints: borrowContextPoints } = getPairPointsInfo(pair, getByAddress)
          const pointDetailRows = getPointDetailRows(pair, getByAddress, borrowContextPoints)

          const destination = getDestination(pair)
          const pairId = getPairId(pair)
          const cardRouteBadge =
            pair.routeType === 'euler'
              ? getEulerBorrowCardRouteBadge(cardSolverConfigs, {
                  borrowVaultAddress: pair.liability,
                  subAccountId: pair.firstSubAccountWithPosition,
                })
              : pair.routeType === 'pooled'
                ? getPooledBorrowCardRouteBadge(cardSolverConfigs, pair.liability)
                : null

          const maxRoe = showLeverageMetrics ? pair.maxRoe : undefined
          const isPositiveRoe = (maxRoe ?? 0) >= 0

          const healthFactor = hasPosition ? pair.healthFactor ?? null : null
          const isLastRow = virtualRow.index === sortedPairs.length - 1

          return (
            <Link
              key={pairId}
              href={destination}
              aria-label={`Open ${pair.collateralSymbol}/${pair.liabilitySymbol} market`}
              className={cn(
                'absolute top-0 left-0 grid w-full cursor-pointer items-center gap-3 px-5 text-inherit no-underline transition-colors hover:bg-white/[0.02]',
                !isLastRow && 'border-white/5 border-b',
              )}
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                gridTemplateColumns: gridCols,
              }}
            >
              <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                  <div className="-space-x-2 flex">
                    <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-neutral-800 ring-2 ring-background">
                      <TokenImage
                        src={collateralImage}
                        tokenAddress={pair.collateralTokenInfo?.address}
                        alt={pair.collateralSymbol || ''}
                        width={32}
                        height={32}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-neutral-800 ring-2 ring-background">
                      <TokenImage
                        src={liabilityImage}
                        tokenAddress={pair.liabilityTokenInfo?.address}
                        alt={pair.liabilitySymbol || ''}
                        width={32}
                        height={32}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4">
                        <TokenImage
                          src={curatorLogo}
                          alt={curatorName}
                          width={14}
                          height={14}
                          className="rounded-full ring-1 ring-background"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContentShort>{curatorName}</TooltipContentShort>
                  </Tooltip>
                </div>
                <div className="flex min-w-0 flex-col">
                  <span className="flex items-center gap-1.5 truncate font-semibold text-foreground text-sm">
                    <span className="truncate">
                      {pair.collateralSymbol} / {pair.liabilitySymbol}
                    </span>
                    <CardRouteBadge badge={cardRouteBadge} />
                  </span>
                  <span className="truncate text-muted-foreground text-xs">{displayName}</span>
                </div>
              </div>

              <div className="flex flex-col items-end justify-center">
                {showLeverageMetrics ? (
                  maxRoe !== undefined ? (
                    <span className={cn('font-semibold text-sm', isPositiveRoe ? 'text-foreground' : 'text-red-400')}>
                      {isPositiveRoe ? '' : '-'}
                      {Math.abs(maxRoe).toFixed(2)}%
                    </span>
                  ) : (
                    <Dash />
                  )
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={cn(
                            'font-medium text-foreground text-sm',
                            hasParallelIncentive && 'drop-shadow-[0_0_10px_rgba(251,229,114,0.22)]',
                          )}
                        >
                          {pair.borrowAPY !== undefined ? `${pair.borrowAPY.toFixed(2)}%` : '0.00%'}
                        </span>
                        {hasParallelIncentive && <ParallelIncentiveBadge />}
                      </div>
                    </TooltipTrigger>
                    <TooltipContentLong>
                      <div className="flex flex-col gap-2 text-xs">
                        {hasParallelIncentive && parallelBorrowIncentives && (
                          <>
                            <div className="flex items-center gap-1 rounded-md border border-[#FBE572]/15 bg-[#FBE572]/[0.06] p-2 text-muted-foreground">
                              <Sparkles className="h-3 w-3 text-gradient-yellow" />

                              <span className="flex items-center gap-1">
                                <span className="font-medium text-green-400">
                                  +{parallelBorrowIncentives.apr.toFixed(2)}% APR
                                </span>
                                <span className="text-muted-foreground">.</span>
                                <span className="text-foreground">
                                  {parallelBorrowIncentives.description.split(' Claim your rewards now.')[0]}
                                </span>{' '}
                                <Link
                                  href={parallelBorrowIncentives.claimUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-medium text-gradient-yellow underline underline-offset-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Claim your rewards now.
                                </Link>
                              </span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between gap-4">
                          <span className="text-muted-foreground">Borrow APY:</span>
                          <span className="text-red-400">-{pair.borrowAPY?.toFixed(2)}%</span>
                        </div>
                        {pair.supplyAPY !== undefined && pair.supplyAPY > 0 && (
                          <div className="flex justify-between gap-4">
                            <span className="text-muted-foreground">Collateral Yield:</span>
                            <span className="text-green-400">+{pair.supplyAPY.toFixed(2)}%</span>
                          </div>
                        )}
                        {netRateDisplay && (
                          <>
                            <div className="my-1 border-white/10 border-t" />
                            <div className="flex justify-between gap-4">
                              <span className="text-muted-foreground">Net Rate:</span>
                              <span className={netRate && netRate >= 0 ? 'text-green-400' : 'text-red-400'}>
                                {netRateDisplay}
                              </span>
                            </div>
                            <div className="mt-1 text-muted-foreground">
                              {netRate && netRate >= 0
                                ? 'You earn more on collateral than you pay to borrow'
                                : 'Your borrow cost exceeds collateral yield'}
                            </div>
                          </>
                        )}
                      </div>
                    </TooltipContentLong>
                  </Tooltip>
                )}
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex flex-col items-end">
                    <span className="font-medium text-foreground text-sm">{maxLTVPercent}%</span>
                    <span className="text-muted-foreground text-xs">Liq: {liqLTVPercent}%</span>
                  </div>
                </TooltipTrigger>
                <TooltipContentLong>
                  <div className="flex flex-col gap-1 text-xs">
                    <div className="flex justify-between gap-4">
                      <span className="text-muted-foreground">Max Borrow LTV:</span>
                      <span>{maxLTVPercent}%</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-muted-foreground">Liquidation LTV:</span>
                      <span>{liqLTVPercent}%</span>
                    </div>
                    <div className="mt-1 border-white/10 border-t pt-1 text-muted-foreground">
                      Position is liquidated when LTV exceeds {liqLTVPercent}%
                    </div>
                  </div>
                </TooltipContentLong>
              </Tooltip>

              <div className="text-right">
                {liquidityDisplay === '-' ? (
                  <Dash />
                ) : (
                  <span className="font-medium text-foreground text-sm">{liquidityDisplay}</span>
                )}
              </div>

              <div className="text-right">
                {pair.utilization !== undefined && !Number.isNaN(pair.utilization) ? (
                  <span className="font-medium text-foreground text-sm">{pair.utilization.toFixed(1)}%</span>
                ) : (
                  <Dash />
                )}
              </div>

              <div className="flex items-center justify-end">
                {hasHypurrPoints || (borrowContextPoints?.length ?? 0) > 0 ? (
                  <Tooltip>
                    <TooltipTrigger
                      asChild
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                    >
                      <div className="cursor-help">
                        <div className="flex items-center justify-center">
                          {hasHypurrPoints && (
                            <div
                              className="relative h-4 w-4 rounded-full border border-background"
                              style={{ zIndex: 10 }}
                            >
                              <Image src={assets.hypurrPawNoShadow} alt="HP" className="h-full w-full" />
                            </div>
                          )}
                          {borrowContextPoints?.map((partner, idx) => (
                            <div
                              key={partner.title}
                              className="relative h-4 w-4 rounded-full border border-background"
                              style={{
                                marginLeft: hasHypurrPoints || idx > 0 ? '-4px' : '0',
                                zIndex: 9 - idx,
                              }}
                            >
                              <Image
                                src={assets[partner.icon as keyof typeof assets]}
                                alt={partner.title}
                                className="h-full w-full rounded-full"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContentLong>
                      <TooltipContentLayout>
                        <TooltipContentLayout.Header>
                          <TooltipContentLayout.Title>Eligible for points</TooltipContentLayout.Title>
                        </TooltipContentLayout.Header>
                        <TooltipContentLayout.Body>
                          {pointDetailRows.map((pointRow) => {
                            return (
                              <div
                                key={`${pointRow.title}-${pointRow.sourceLabel}`}
                                className="mt-2 flex items-center gap-2"
                              >
                                <Image
                                  src={
                                    pointRow.icon === 'hypurrPawNoShadow'
                                      ? assets.hypurrPawNoShadow
                                      : assets[pointRow.icon as keyof typeof assets]
                                  }
                                  alt={pointRow.title}
                                  className="h-4 w-4 rounded-full"
                                />
                                <span className="text-sm">
                                  {pointRow.multiplier ? `${pointRow.multiplier}x ` : ''}
                                  {pointRow.title} ({pointRow.sourceLabel})
                                </span>
                              </div>
                            )
                          })}
                        </TooltipContentLayout.Body>
                      </TooltipContentLayout>
                    </TooltipContentLong>
                  </Tooltip>
                ) : (
                  <Dash />
                )}
              </div>

              {showInWallet && <InWalletCell amount={pair.walletBalanceAmount} symbol={pair.collateralSymbol} />}

              {showBorrowed && (
                <div className="flex items-center justify-end">
                  {hasPosition ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex cursor-help flex-col items-end">
                          {pair.userBorrowAmountUsd !== undefined && pair.userBorrowAmountUsd > 0 ? (
                            <span className="font-medium text-foreground text-sm">
                              $
                              {pair.userBorrowAmountUsd.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          ) : (
                            <Dash />
                          )}
                          <span className="text-muted-foreground text-xs">
                            {pair.userBorrowAmount?.toFixed(4)} {pair.liabilitySymbol}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContentLong>
                        <div className="flex flex-col gap-1 text-xs">
                          {healthFactor !== null && (
                            <>
                              <div
                                className={cn(
                                  'font-medium',
                                  healthFactor <= 1.1 && 'text-red-400',
                                  healthFactor > 1.1 && healthFactor <= 1.5 && 'text-yellow-400',
                                  healthFactor > 1.5 && 'text-green-400',
                                )}
                              >
                                {healthFactor <= 1.1 && 'Position at risk of liquidation'}
                                {healthFactor > 1.1 && healthFactor <= 1.5 && 'Position approaching liquidation'}
                                {healthFactor > 1.5 && 'Position is healthy'}
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-muted-foreground">Health Factor:</span>
                                <span
                                  className={cn(
                                    healthFactor <= 1.1 && 'text-red-400',
                                    healthFactor > 1.1 && healthFactor <= 1.5 && 'text-yellow-400',
                                    healthFactor > 1.5 && 'text-green-400',
                                  )}
                                >
                                  {healthFactor.toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}
                          <div className="flex justify-between gap-4">
                            <span className="text-muted-foreground">Collateral:</span>
                            <span>
                              {pair.userCollateralDeposit?.toFixed(4)} {pair.collateralSymbol}
                            </span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-muted-foreground">Borrowed:</span>
                            <span>
                              {pair.userBorrowAmount?.toFixed(4)} {pair.liabilitySymbol}
                            </span>
                          </div>
                          {pair.userCollateralDepositUsd && pair.userBorrowAmountUsd && (
                            <div className="flex justify-between gap-4">
                              <span className="text-muted-foreground">Current LTV:</span>
                              <span>
                                {((pair.userBorrowAmountUsd / pair.userCollateralDepositUsd) * 100).toFixed(1)}%
                              </span>
                            </div>
                          )}
                        </div>
                      </TooltipContentLong>
                    </Tooltip>
                  ) : (
                    <Dash />
                  )}
                </div>
              )}

            </Link>
          )
        })}
      </div>

      {sortedPairs.length === 0 && (
        <div className="flex items-center justify-center p-8 text-muted-foreground">No borrow pairs found</div>
      )}
    </div>
  )
}
